import {
	Col,
	DatePicker,
	Divider,
	Form,
	Input,
	InputNumber,
	Row,
	Select,
} from 'antd';
import { FC, useEffect, useMemo } from 'react';
import FleetModal from '@/libs/antd/components/Modal';
import FleetTitle from '@/libs/antd/components/Title';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import { useForm } from '@/libs/antd/form';
import { rules } from '@/libs/antd/rules';
import { inputKtp } from '@/libs/antd/input';
import { inputNPWP } from '@/libs/antd/input-number';
import { useSelectAddress, useSelectByEndpoint } from '@/libs/fleet-api/hooks/master-data';
import type { Dayjs } from 'dayjs';

// Inline lookup-by-endpoint Select. Mirrors LookupSelect in QCAccordionForm.
// Inlined here to avoid a circular import (QCAccordionForm already imports this modal).
const InlineLookupSelect: FC<{
	endpoint: string;
	value?: string;
	onChange?: (value: string | undefined) => void;
	id?: string;
}> = ({ endpoint, value, onChange, id }) => {
	const selectProps = useSelectByEndpoint(endpoint);
	const options = useMemo(() => {
		const fetched = (selectProps.options ?? []) as { label: string; value: string }[];
		if (!value) return fetched;
		const exists = fetched.some((o) => o.value === value);
		return exists ? fetched : [{ label: value, value }, ...fetched];
	}, [selectProps.options, value]);
	return (
		<Select
			{...selectProps}
			id={id}
			options={options}
			value={value}
			onChange={onChange}
			placeholder='Ketik 3 digit kode pos...'
		/>
	);
};

type AddressMasterData = {
	postalId: number;
	postalCode: string;
	locationName: string;
	districtName: string;
	cityName: string;
};

export type PemilikManfaatFormValues = {
	hubunganDenganCustomer: string;
	nama: string;
	nik: string;
	npwp: string;
	tempatLahir: string;
	tanggalLahir: Dayjs | null;
	kewarganegaraan: string;
	negaraAsal: string;
	jenisKelamin: string;
	statusPerkawinan: string;
	kodePos: string;
	kelurahan: string;
	kecamatan: string;
	kota: string;
	addressPemilikManfaat?: AddressMasterData;
	jenisPekerjaan: string;
	namaPerusahaan: string;
};

export type PemilikManfaatOptions = {
	relationship: { label: string; value: string }[];
	nationality: { label: string; value: string }[];
	gender: { label: string; value: string }[];
	maritalStatus: { label: string; value: string }[];
};

type QCPemilikManfaatModalProps = {
	open: boolean;
	mode: 'create' | 'edit';
	readOnly?: boolean;
	record?: Partial<PemilikManfaatFormValues>;
	options: PemilikManfaatOptions;
	onClose: () => void;
	onSave: (values: PemilikManfaatFormValues) => void;
};

// Hides the displayed value when it doesn't match any option, so Select renders blank instead of raw "0".
const blankIfUnmatched = (options: { label: string; value: string }[]) => (value: unknown) => {
	if (value == null || value === '') return { value: undefined };
	return options.some((o) => o.value === String(value)) ? { value } : { value: undefined };
};

const QCPemilikManfaatModal: FC<QCPemilikManfaatModalProps> = ({ open, mode, readOnly, record, options, onClose, onSave }) => {
	const { form } = useForm<PemilikManfaatFormValues>();
	const selectAddress = useSelectAddress();

	// Hydrate form when modal opens with a record. Build composite address from flat fields so the
	// postal Select can show the saved postalCode label (postalId is unknown until user re-selects).
	useEffect(() => {
		if (open && record) {
			const composite: AddressMasterData | undefined =
				record.kodePos || record.kelurahan || record.kecamatan || record.kota
					? {
							postalId: 0,
							postalCode: record.kodePos ?? '',
							locationName: record.kelurahan ?? '',
							districtName: record.kecamatan ?? '',
							cityName: record.kota ?? '',
						}
					: undefined;
			form.setFieldsValue({ ...record, addressPemilikManfaat: composite });
		}
	}, [open, record, form]);

	// When user picks a new postal in the Select, mirror the chosen address into the flat fields
	// (kodePos / kelurahan / kecamatan / kota) so submit payload stays unchanged.
	const addressPemilikManfaat = Form.useWatch('addressPemilikManfaat', form) as AddressMasterData | undefined;
	useEffect(() => {
		if (!addressPemilikManfaat) return;
		form.setFieldsValue({
			kodePos: addressPemilikManfaat.postalCode ?? '',
			kelurahan: addressPemilikManfaat.locationName ?? '',
			kecamatan: addressPemilikManfaat.districtName ?? '',
			kota: addressPemilikManfaat.cityName ?? '',
		});
	}, [addressPemilikManfaat, form]);

	return (
		<Form
			{...ANTD_DEFAULT_PROPS.FORM}
			form={form}
			labelCol={{ span: 10 }}
			wrapperCol={{ span: 14 }}
			disabled={readOnly}
			onFinish={(values) => {
				// Strip the composite helper before handing values to the parent.
				const { addressPemilikManfaat: _composite, ...flat } = values;
				void _composite;
				onSave(flat as PemilikManfaatFormValues);
			}}
		>
			<FleetModal
				open={open}
				width={760}
				buttonPosition='end'
				closeIcon={false}
				keyboard={false}
				maskClosable={false}
				title={
					<FleetTitle level={1} marginBottom={0}>
						{readOnly ? 'Detail Pemilik Manfaat' : mode === 'create' ? 'Tambah Pemilik Manfaat' : 'Edit Pemilik Manfaat'}
					</FleetTitle>
				}
				styles={{
					header: { padding: 0, border: 'none' },
					body: { paddingTop: 20 },
				}}
				onOk={() => form.submit()}
				onCancel={onClose}
				afterClose={() => form.resetFields()}
				okText='SAVE'
				cancelText='BACK'
				okButtonProps={readOnly ? { style: { display: 'none' } } : undefined}
			>
				{/* Hubungan Dengan Customer */}
				<Row gutter={[40, 20]}>
					<Col span={24}>
						<Form.Item
							name='hubunganDenganCustomer'
							label='Hubungan Dengan Customer'
							labelCol={{ span: 5 }}
							wrapperCol={{ span: 19 }}
							rules={[{ required: true, message: 'Hubungan wajib diisi' }]}
							getValueProps={blankIfUnmatched(options.relationship)}
						>
							<Select allowClear options={options.relationship} />
						</Form.Item>
					</Col>
				</Row>
				<Divider />

				{/* Nama + NIK */}
				<Row gutter={[40, 20]}>
					<Col span={12}>
						<Form.Item name='nama' label='Nama' rules={[{ required: true, message: 'Nama wajib diisi' }]}>
							<Input />
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item name='nik' label='NIK' rules={rules.ktp}>
							<Input {...inputKtp} />
						</Form.Item>
					</Col>
				</Row>
				<Divider />

				{/* NPWP + Tempat Lahir */}
				<Row gutter={[40, 20]}>
					<Col span={12}>
						<Form.Item name='npwp' label='NPWP' rules={rules.npwp}>
							<InputNumber {...inputNPWP} style={{ width: '100%' }} />
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item name='tempatLahir' label='Tempat Lahir' rules={[{ required: true, message: 'Tempat lahir wajib diisi' }]}>
							<InlineLookupSelect endpoint='/api/master-city/get-list' />
						</Form.Item>
					</Col>
				</Row>
				<Divider />

				{/* Tanggal Lahir + Kewarganegaraan */}
				<Row gutter={[40, 20]}>
					<Col span={12}>
						<Form.Item name='tanggalLahir' label='Tanggal Lahir' rules={[{ required: true, message: 'Tanggal lahir wajib diisi' }]}>
							<DatePicker style={{ width: '100%' }} format='DD/MM/YYYY' />
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							name='kewarganegaraan'
							label='Kewarganegaraan'
							rules={[{ required: true, message: 'Kewarganegaraan wajib diisi' }]}
							getValueProps={blankIfUnmatched(options.nationality)}
						>
							<Select allowClear options={options.nationality} />
						</Form.Item>
					</Col>
				</Row>
				<Divider />

				{/* Negara Asal + Jenis Kelamin */}
				<Row gutter={[40, 20]}>
					<Col span={12}>
						<Form.Item name='negaraAsal' label='Negara Asal (Jika WNA)'>
							<InlineLookupSelect endpoint='/api/master-country/get-list' />
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							name='jenisKelamin'
							label='Jenis Kelamin'
							rules={[{ required: true, message: 'Jenis kelamin wajib diisi' }]}
							getValueProps={blankIfUnmatched(options.gender)}
						>
							<Select allowClear options={options.gender} />
						</Form.Item>
					</Col>
				</Row>
				<Divider />

				{/* Status Perkawinan + Kode Pos (search lookup) */}
				<Row gutter={[40, 20]}>
					<Col span={12}>
						<Form.Item
							name='statusPerkawinan'
							label='Status Perkawinan'
							rules={[{ required: true, message: 'Status perkawinan wajib diisi' }]}
							getValueProps={blankIfUnmatched(options.maritalStatus)}
						>
							<Select allowClear options={options.maritalStatus} />
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							name='addressPemilikManfaat'
							label='Kode Pos Sesuai Identitas'
							rules={[{ required: true, message: 'Kode pos wajib diisi' }]}
							getValueFromEvent={(_, option) => (option as { data?: AddressMasterData } | undefined)?.data}
							getValueProps={(value: AddressMasterData | undefined) => {
								if (!value || !value.postalCode) return {};
								return { value: { value: value.postalId, label: value.postalCode } };
							}}
						>
							<Select {...selectAddress} placeholder='Ketik 3 digit kode pos...' />
						</Form.Item>
					</Col>
				</Row>
				<Divider />

				{/* Kelurahan + Kecamatan — derived from postal, non-editable */}
				<Row gutter={[40, 20]}>
					<Col span={12}>
						<Form.Item name='kelurahan' label='Kelurahan Sesuai Identitas' rules={[{ required: true, message: 'Kelurahan wajib diisi' }]}>
							<Input disabled />
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item name='kecamatan' label='Kecamatan' rules={[{ required: true, message: 'Kecamatan wajib diisi' }]}>
							<Input disabled />
						</Form.Item>
					</Col>
				</Row>
				<Divider />

				{/* Kota + Jenis Pekerjaan */}
				<Row gutter={[40, 20]}>
					<Col span={12}>
						<Form.Item name='kota' label='Kota' rules={[{ required: true, message: 'Kota wajib diisi' }]}>
							<Input disabled />
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item name='jenisPekerjaan' label='Jenis Pekerjaan' rules={[{ required: true, message: 'Jenis pekerjaan wajib diisi' }]}>
							<Input />
						</Form.Item>
					</Col>
				</Row>
				<Divider />

				{/* Nama Perusahaan */}
				<Row gutter={[40, 20]}>
					<Col span={24}>
						<Form.Item
							name='namaPerusahaan'
							label='Nama Perusahaan / Usaha Profesi'
							labelCol={{ span: 5 }}
							wrapperCol={{ span: 19 }}
							rules={[{ required: true, message: 'Nama perusahaan wajib diisi' }]}
						>
							<Input />
						</Form.Item>
					</Col>
				</Row>
			</FleetModal>
		</Form>
	);
};

export default QCPemilikManfaatModal;
